import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  Drawer,
  Badge,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  AddShoppingCart as AddShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import CertificateLink from '../components/CertificateLink';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useFeedback } from '../context/FeedbackContext';
import { DIAMOND_TYPES, formatGradeShort } from '../constants/appConstants';
import axios from 'axios';
import { TABLE_ROWS_PREF_KEYS, pickRowsPerPageFromPrefs, saveTableRowsPreference } from '../utils/tablePagePreferences';
import SelectField from '../components/inputs/SelectField';
import { inventoryShapeOptions } from '../utils/shapeOptions';

const ClientPortal = () => {
  const { isClient } = useAuth();
  const { addToCart, isInCart } = useCart();
  const { showSuccess } = useFeedback();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [diamonds, setDiamonds] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 20 : 50);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    shape: '',
    color: [],
    clarity: [],
    minCarat: '',
    maxCarat: '',
  });
  const [filterOptions, setFilterOptions] = useState({});
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

  const clientFiltersActiveCount = useMemo(() => {
    let n = 0;
    if (filters.shape) n += 1;
    if (filters.color?.length) n += 1;
    if (filters.clarity?.length) n += 1;
    if (filters.minCarat) n += 1;
    if (filters.maxCarat) n += 1;
    return n;
  }, [filters]);

  const fetchDiamonds = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set('page', (page + 1).toString());
      params.set('limit', rowsPerPage.toString());
      Object.entries(filters).forEach(([key, value]) => {
        if (value === '' || (Array.isArray(value) && value.length === 0)) return;
        if (Array.isArray(value)) {
          params.set(key, value.join(','));
        } else {
          params.set(key, value);
        }
      });

      const response = await axios.get(`/api/diamonds?${params}`);
      setDiamonds(response.data.diamonds);
      setTotal(response.data.pagination.total);
    } catch (error) {
      console.error('Failed to fetch diamonds:', error);
    }
  }, [page, rowsPerPage, filters]);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get('/api/diamonds/filters/options');
      setFilterOptions({
        ...response.data,
        shapes: inventoryShapeOptions(response.data?.certifiedShapes ?? response.data?.shapes),
        certifiedShapes: inventoryShapeOptions(response.data?.certifiedShapes ?? response.data?.shapes),
      });
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/api/users/preferences');
        if (cancelled) return;
        const fallback = isMobile ? 20 : 50;
        setRowsPerPage(pickRowsPerPageFromPrefs(data || {}, TABLE_ROWS_PREF_KEYS.clientPortal, [10, 20, 50, 100], fallback));
      } catch (_) {
        /* keep default from useState */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isClient) {
      fetchDiamonds();
      fetchFilterOptions();
    }
  }, [isClient, fetchDiamonds]);

  useEffect(() => {
    if (!isMobile) setFiltersDrawerOpen(false);
  }, [isMobile]);

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPage(0);
  };

  const handleAddToCart = (diamond) => {
    addToCart({
      type: DIAMOND_TYPES.CERTIFIED,
      diamond,
    });
    showSuccess(`${diamond.certificateNumber} added to cart`);
  };

  if (!isClient) {
    return (
      <Layout>
        <Container>
          <Alert severity="error">This page is only available for client companies.</Alert>
        </Container>
      </Layout>
    );
  }

  const renderClientPortalFilterFields = () => (
    <Box display="flex" flexDirection="column" gap={2} sx={{ width: '100%' }}>
      <SelectField
        label="Shape"
        fullWidth
        value={filters.shape}
        onChange={(next) => handleFilterChange('shape', next)}
        options={(filterOptions.shapes || []).map((shape) => {
          const s = String(shape).toUpperCase();
          return { value: s, label: s };
        })}
        emptyLabel="All"
        includeEmptyOption
      />
      <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'flex-start' }}>
        <SelectField
          label="Color"
          multiple
          value={filters.color}
          onChange={(next) => handleFilterChange('color', next)}
          options={filterOptions.colors || []}
          emptyLabel="All"
          sx={{ flex: 1, minWidth: 0 }}
        />
        <SelectField
          label="Clarity"
          multiple
          value={filters.clarity}
          onChange={(next) => handleFilterChange('clarity', next)}
          options={filterOptions.clarities || []}
          emptyLabel="All"
          sx={{ flex: 1, minWidth: 0 }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
        <TextField
          size="small"
          label="Min Carat"
          type="number"
          value={filters.minCarat}
          onChange={(e) => handleFilterChange('minCarat', e.target.value)}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <TextField
          size="small"
          label="Max Carat"
          type="number"
          value={filters.maxCarat}
          onChange={(e) => handleFilterChange('maxCarat', e.target.value)}
          sx={{ flex: 1, minWidth: 0 }}
        />
      </Box>
    </Box>
  );

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
        >
          Available Diamonds
        </Typography>

        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          {isMobile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Filters">
                <IconButton color="primary" onClick={() => setFiltersDrawerOpen(true)} aria-label="Open filters" size="large">
                  <Badge color="error" badgeContent={clientFiltersActiveCount} invisible={clientFiltersActiveCount === 0}>
                    <FilterListIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                Tap to filter diamonds. Results update as you change options.
              </Typography>
            </Box>
          ) : (
            <Box display="flex" gap={{ xs: 1, sm: 2 }} flexWrap="wrap" flexDirection="row">
              <SelectField
                label="Shape"
                value={filters.shape}
                onChange={(next) => handleFilterChange('shape', next)}
                options={(filterOptions.shapes || []).map((shape) => {
                  const s = String(shape).toUpperCase();
                  return { value: s, label: s };
                })}
                emptyLabel="All"
                includeEmptyOption
                sx={{ minWidth: { xs: '100%', sm: 150 } }}
              />
              <SelectField
                label="Color"
                multiple
                value={filters.color}
                onChange={(next) => handleFilterChange('color', next)}
                options={filterOptions.colors || []}
                emptyLabel="All"
                sx={{ minWidth: { xs: '100%', sm: 180 } }}
              />
              <SelectField
                label="Clarity"
                multiple
                value={filters.clarity}
                onChange={(next) => handleFilterChange('clarity', next)}
                options={filterOptions.clarities || []}
                emptyLabel="All"
                sx={{ minWidth: { xs: '100%', sm: 180 } }}
              />
              <TextField
                size="small"
                label="Min Carat"
                type="number"
                value={filters.minCarat}
                onChange={(e) => handleFilterChange('minCarat', e.target.value)}
                sx={{ width: { xs: '100%', sm: 120 } }}
              />
              <TextField
                size="small"
                label="Max Carat"
                type="number"
                value={filters.maxCarat}
                onChange={(e) => handleFilterChange('maxCarat', e.target.value)}
                sx={{ width: { xs: '100%', sm: 120 } }}
              />
            </Box>
          )}
        </Paper>

        {isMobile && (
          <Drawer
            anchor="right"
            open={filtersDrawerOpen}
            onClose={() => setFiltersDrawerOpen(false)}
            PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, maxWidth: '100vw' } }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" component="span">Filters</Typography>
                <IconButton edge="end" onClick={() => setFiltersDrawerOpen(false)} aria-label="Close filters">
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2, minHeight: 0 }}>
                {renderClientPortalFilterFields()}
              </Box>
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Button fullWidth variant="contained" onClick={() => setFiltersDrawerOpen(false)}>
                  Done
                </Button>
              </Box>
            </Box>
          </Drawer>
        )}

        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {diamonds.map((diamond) => (
              <Card key={diamond._id} elevation={2}>
                <CardContent>
                  <Box mb={0.5}>
                    <CertificateLink certificateNumber={diamond.certificateNumber} lab={diamond.lab} variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 'bold' }} />
                  </Box>
                  <Grid container spacing={1} sx={{ mt: 0.5, '& .MuiTypography-caption': { fontSize: '0.8rem' }, '& .MuiTypography-body2': { fontSize: '0.9rem' } }}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Shape</Typography>
                      <Typography variant="body2">{diamond.shape}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Carat</Typography>
                      <Typography variant="body2">{diamond.carat}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Type</Typography>
                      <Typography variant="body2">{diamond.diamondType || '—'}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Color</Typography>
                      <Typography variant="body2">{diamond.color}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Clarity</Typography>
                      <Typography variant="body2">{diamond.clarity}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Lab</Typography>
                      <Typography variant="body2">{diamond.lab}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Cut</Typography>
                      <Typography variant="body2">{formatGradeShort(diamond.cut) || '—'}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Symmetry</Typography>
                      <Typography variant="body2">{formatGradeShort(diamond.symmetry) || '—'}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Polish</Typography>
                      <Typography variant="body2">{formatGradeShort(diamond.polish) || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
                        <Box>
                          <Typography variant="caption" color="text.secondary">Measurements (mm)</Typography>
                          <Typography variant="body2">
                            {diamond.length != null && diamond.width != null && diamond.height != null
                              ? `${diamond.length} × ${diamond.width} × ${diamond.height}`
                              : '—'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Ratio</Typography>
                          <Typography variant="body2">
                            {diamond.ratio != null ? diamond.ratio : (diamond.length != null && diamond.width != null && Number(diamond.width) > 0
                              ? (Math.round((diamond.length / diamond.width) * 100) / 100).toFixed(2)
                              : '—')}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" color="text.secondary">Price</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          ${diamond.price?.toLocaleString()}
                        </Typography>
                      </Box>
                      {isInCart(diamond._id) ? (
                        <Chip label="In Cart" color="success" size="small" icon={<CheckCircleIcon />} />
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddShoppingCartIcon />}
                          onClick={() => handleAddToCart(diamond)}
                        >
                          Add to Cart
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Certificate #</TableCell>
                  <TableCell>Shape</TableCell>
                  <TableCell>Carat</TableCell>
                  <TableCell>Measurements</TableCell>
                  <TableCell>Ratio</TableCell>
                  <TableCell>Color</TableCell>
                  <TableCell>Clarity</TableCell>
                  <TableCell>Cut</TableCell>
                  <TableCell>Polish</TableCell>
                  <TableCell>Symmetry</TableCell>
                  <TableCell>Lab</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {diamonds.map((diamond) => (
                  <TableRow key={diamond._id}>
                    <TableCell>
                      <CertificateLink certificateNumber={diamond.certificateNumber} lab={diamond.lab} variant="body2" />
                    </TableCell>
                    <TableCell>{diamond.shape}</TableCell>
                    <TableCell>{diamond.carat}</TableCell>
                    <TableCell>
                      {diamond.length != null && diamond.width != null && diamond.height != null
                        ? `${diamond.length}×${diamond.width}×${diamond.height} mm`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {diamond.ratio != null ? diamond.ratio : (diamond.length != null && diamond.width != null && Number(diamond.width) > 0
                        ? (Math.round((diamond.length / diamond.width) * 100) / 100).toFixed(2)
                        : '—')}
                    </TableCell>
                    <TableCell>{diamond.color}</TableCell>
                    <TableCell>{diamond.clarity}</TableCell>
                    <TableCell>{formatGradeShort(diamond.cut) || '—'}</TableCell>
                    <TableCell>{formatGradeShort(diamond.polish) || '—'}</TableCell>
                    <TableCell>{formatGradeShort(diamond.symmetry) || '—'}</TableCell>
                    <TableCell>{diamond.lab}</TableCell>
                    <TableCell>{diamond.diamondType || '—'}</TableCell>
                    <TableCell>${diamond.price?.toLocaleString()}</TableCell>
                    <TableCell>
                      {isInCart(diamond._id) ? (
                        <Chip label="In Cart" color="success" size="small" icon={<CheckCircleIcon />} />
                      ) : (
                        <Tooltip title="Add to Cart">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleAddToCart(diamond)}
                            sx={{ border: '1px solid', borderColor: 'primary.main' }}
                          >
                            <AddShoppingCartIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            const next = parseInt(e.target.value, 10);
            setRowsPerPage(next);
            setPage(0);
            saveTableRowsPreference(TABLE_ROWS_PREF_KEYS.clientPortal, next).catch(() => {});
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          sx={{ 
            '& .MuiTablePagination-toolbar': {
              flexWrap: 'wrap',
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            },
          }}
        />
      </Container>
    </Layout>
  );
};

export default ClientPortal;
